import React, {useId, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, CaretDown, CaretUp, CheckCircle, Info, Target} from '../../icons';
import {Spinner} from '../../Spinner';
import {dayOfPeriodLabel} from './panelFormatters';
import {lineDefinition, lineDefinitionMore, lineDefinitionTitle} from './lineDefinition';
import {HELP_LINE_DEFINITION, LinkToArticle} from '../../help_center/LinkToArticle';
import {FILTER_INACTIVE} from './linesViewLogic';
import {ControlLineRow, lineChannels} from './ControlLineRow';
import {lineIsFinished} from './LineVisuals';
import {MediaPlanStrip} from './MediaPlanStrip';
import {economyLineFor} from './economyFormatters';
import {anchorAttr} from '../../../guides/anchors';
import {alertLineIds} from '../../alerts_center/shared/alertLines';
import {UncontrolledSummaryCard} from '../../alerts_center/uncontrolled/UncontrolledSummaryCard';
import './ControlLines.scss';

// Cuántas líneas entran en el Panel antes de mandar a Monitoreo. El Panel es el resumen del
// cliente: una card con quince filas deja de ser un resumen y duplica la pantalla de Monitoreo,
// que es la que está pensada para trabajar sobre TODAS las líneas.
const MAX_LINES = 6;

// Una línea eventual con el período ya cerrado (la misma regla que usa la vista de líneas).
const isFinished = lineIsFinished;

// Logos de 18px con 4px de gap (`.pnl-line__chans`).
const CHANNEL_LOGO_PX = 18;
const CHANNEL_GAP_PX = 4;

/**
 * Cuánto ancho reservar para los logos: el de la fila con MÁS canales del bloque.
 *
 * La reserva existe para que los nombres arranquen todos a la misma altura (QA-VIS-panel-02: sin
 * ella quedaban en escalera). Pero era fija en 4 logos (84px), y con líneas de uno o dos canales
 * —lo normal— dejaba un hueco grueso entre los íconos y el nombre (card 6tGAWS1a). Medida sobre
 * las filas visibles, la alineación se conserva y el hueco desaparece.
 */
function channelsWidth(lines) {
    const most = Math.max(0, ...lines.map((line) => lineChannels(line).length));
    return most > 0 ? most * CHANNEL_LOGO_PX + (most - 1) * CHANNEL_GAP_PX : 0;
}

// `dayOfPeriodLabel` es puro y no conoce el copy: se le pasa un `t` envuelto con el default para
// que la leyenda nunca muestre la key cruda (la clave ya vive en es/en.json; el default es la red).
function withDayDefault(t) {
    return (key, params) => t(key, {...params, defaultValue: 'día {{day}} de {{days}}'});
}

/**
 * Las líneas de control del cliente (`panel.lines`, sólo activas y ya ordenadas por severidad
 * desde el backend). Cada fila es un `ControlLineRow`.
 *
 * El orden NO se recalcula acá: el backend manda CRITIC → BAD → sin dato → OK, y esa es la misma
 * unidad de veredicto que dispara las alertas de la agencia. Reordenar en el cliente sería
 * inventar un segundo criterio que tarde o temprano contradice al primero.
 */
export function ControlLinesCard({data, loading, clientId, isNewClient, latestAlert, month,
                                  onNewLine, mediaPlanEnabled, onDefineGoal, onDeactivateLine,
                                  inactiveCount = 0, mediaPlan, projection, currency, economy = null}) {
    const {t} = useTranslation();
    // El grupo de terminadas arranca CERRADO: son hechos consumados, no seguimiento del día. `null`
    // = el usuario todavía no lo tocó, y ahí decide la alerta viva (ver `groupOpen` más abajo).
    const [finishedOpen, setFinishedOpen] = useState(null);
    // El texto del cartel del ⓘ de la cabecera: lo describe para el lector de pantalla.
    const definitionId = useId();

    if (loading) {
        return (
            <div className="box pnl-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!data) return null;

    const lines = data;
    /**
     * Las TERMINADAS van aparte (card XLWKaxjN, 23/09): una línea eventual cuyo período ya cerró
     * (`projection.reason === 'FINISHED'`) sigue activa —no la desactivó nadie— pero no hay nada
     * que seguir en ella, y mezclada con las que están corriendo le robaba un lugar de las seis y la
     * atención a una fila viva. Van en un grupo cerrado al pie, en el mismo orden del backend.
     * Si TODAS terminaron, se muestran sueltas: un bloque con el grupo cerrado y nada más arriba
     * parecería vacío.
     */
    const ongoing = lines.filter((line) => !isFinished(line));
    const finished = lines.filter(isFinished);
    const groupFinished = ongoing.length > 0 && finished.length > 0;
    const visible = (groupFinished ? ongoing : lines).slice(0, MAX_LINES);
    const hidden = (groupFinished ? ongoing : lines).length - visible.length;
    // La alerta viva del cliente se ancla en SU fila para que el KPI y el bloque digan lo mismo. Si
    // esa fila es una terminada, el grupo arranca ABIERTO: cerrado, el KPI nombraría algo que el
    // bloque esconde (C-LINEAS-04 del review). Una vez que el usuario lo toca, manda su gesto.
    // Una alerta puede ser de varias líneas que miran lo mismo (DP7i8uTf): se ancla en todas.
    const alertLines = alertLineIds(latestAlert);
    const alertInFinished = finished.some((line) => alertLines.includes(line.id));
    const groupOpen = finishedOpen === null ? alertInFinished : finishedOpen;
    const shownFinished = groupFinished && groupOpen ? finished : [];
    // El reemplazo de la pestaña Monitoreo (card gtAL5axI): la vista de todas las líneas del
    // propio Panel. Los CTAs de esta card ya no salen de la pantalla.
    const linesPath = `/client/${clientId}/lines`;
    // El historial de las líneas (cierres mes a mes: presupuesto, costo, conversiones, objetivos)
    // hoy sólo se alcanza desde Monitoreo, que se va a eliminar (card Fa9hRD9I). Su pantalla ya es
    // una RUTA PROPIA del cliente, así que el panel la linkea directo: cuando Monitoreo muera, se
    // borra el link de al lado y el historial sigue teniendo entrada.
    const historyPath = `/client/${clientId}/history`;

    // La leyenda es del TICK de las barras, y el tick se posiciona contra el MES del cliente
    // (`kpis.month`, mes calendario en la tz del request): tiene que salir de la MISMA fuente que el
    // KPI de inversión. Sacarla del período de la primera línea ponía dos "día X de Y" distintos en
    // la misma pantalla (el KPI decía "día 31 de 31" y el pie "día 15 de 31") y el usuario no sabe a
    // cuál creerle. Si una línea tiene período propio, su desfase se expresa en SU fila.
    const monthData = month || {};
    const dayLabel = dayOfPeriodLabel(monthData.day, monthData.days_in_month, withDayDefault(t));

    /**
     * "Sincronizar con planilla de media plan" — la OTRA vía de carga de líneas.
     *
     * Vive acá y no como una sección aparte porque, en la práctica, un media plan vinculado es una
     * forma de crear líneas de control automáticamente: el usuario que quiere cargar líneas tiene
     * que encontrar las dos opciones en el mismo lugar (feedback de Pablo sobre el mockup v2). El
     * botón lleva a la pantalla de Media Plan, que sigue existiendo entera — acá sólo se suma la
     * puerta de entrada que faltaba.
     *
     * Sólo aparece si la agencia tiene la capability: sin ella el destino es un 404.
     */
    // Rótulo: «Sincronizar con planilla de media plan», no «Importar» (card 6tGAWS1a). El media plan
    // no se carga una vez: queda vinculado y sus cambios siguen llegando como propuestas.
    // Con la planilla ya vinculada el botón no se ofrece: la franja de abajo dice cuál es y lleva a
    // ella (card gW32VovP).
    const mediaPlanConnected = !!(mediaPlan && mediaPlan.enabled !== false && mediaPlan.connected);
    const renderImportMediaPlan = () => {
        if (!mediaPlanEnabled || mediaPlanConnected) return null;
        return (
            <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm" to={`/client/${clientId}/media-plan`}>
                <ArrowsClockwise size={14} weight="bold"/>
                {t('client_panel.lines_import_media_plan', 'Sincronizar con planilla de media plan')}
            </Link>
        );
    };

    const renderHead = () => (
        <div className="pnl-card__head">
            {/* Título + contador + ⓘ van en un grupo que es el ANCLA del cartel del ⓘ: anclado a la
                cabecera entera, cuando ésta se parte en dos o tres renglones (1280 con el sidebar
                expandido, 390) el cartel caía 47 a 85 px debajo del ícono, con «Ver todas» en el
                medio (QA-VIS-SEGFE-01). El grupo es siempre un renglón. */}
            <span className="pnl-lines__title-group">
                <h2 className="pnl-card__title">{t('client_panel.lines_title', 'Líneas de seguimiento')}</h2>
                {/* Contador de la cabecera (convención de las cards del Panel). */}
                <span className="pnl-cnt">({lines.length})</span>
                {/* Qué es una línea, con la definición única del producto (card mFHzKeNK). Reemplaza
                    a la etiqueta «Alimentan el Tablero y la IA» (card 6tGAWS1a), que decía para qué
                    servían sin decir qué eran. El cartel se ancla al GRUPO y no al ⓘ: pegado al
                    título, a 390px un cartel de 340px anclado al ícono se salía por la derecha.
                    `role="img"`: un `aria-label` en un `span` genérico no está admitido por ARIA 1.2
                    y varios lectores lo ignoran (C-SEGFE-06 del review); la definición va por
                    `aria-describedby`, como en el chip del objetivo. */}
                <span className="info-icon-container pnl-lines__info" tabIndex={0} role="img"
                      aria-label={lineDefinitionTitle(t)} aria-describedby={definitionId}>
                    <Info size={14} weight="bold"/>
                    <span className="warning-tooltip -rich">
                        <span className="warning-tooltip-body">
                            <span className="pnl-lines__tip">
                                <span className="pnl-lines__tip-title">{lineDefinitionTitle(t)}</span>
                                <span className="pnl-lines__tip-text" id={definitionId}>{lineDefinition(t)}</span>
                            </span>
                        </span>
                    </span>
                </span>
            </span>
            <span className="pnl-card__spacer"/>
            {/* Sin líneas, el vacío ya ofrece las dos vías juntas (a mano y desde el media plan):
                repetir el botón en la cabecera lo mostraba dos veces (QA-VIS-04). */}
            {lines.length > 0 && renderImportMediaPlan()}
            {/* La vista de todas las líneas (reemplazo de Monitoreo). «Ver todas» y no «Ver las 8»
                (card XLWKaxjN): el número contaba sólo las activas, así que al desactivar una pasaba
                a «las 7» y la desactivada parecía haberse perdido. Aparece también cuando sólo hay
                desactivadas: es la puerta para reactivarlas. */}
            {(lines.length > 0 || inactiveCount > 0) && (
                <Link className="pnl-card__link" to={linesPath}>
                    {t('client_panel.lines_view_all', 'Ver todas →')}
                </Link>
            )}
        </div>
    );

    // Las desactivadas no se muestran en el Panel (no sincronizan), pero tienen que poder
    // encontrarse: Pablo desactivó «Black Monday» y no supo dónde había ido a parar (card XLWKaxjN).
    // Lleva directo al filtro de la vista de líneas, donde se reactivan.
    const renderInactiveLink = () => {
        if (!(inactiveCount > 0)) return null;
        return (
            <Link className="pnl-lines__more" to={`${linesPath}?filtro=${FILTER_INACTIVE}`}>
                {t('client_panel.lines_inactive_link', {
                    count: inactiveCount,
                    defaultValue: '{{count}} desactivada →',
                    defaultValue_other: '{{count}} desactivadas →',
                })}
            </Link>
        );
    };

    const renderRow = (line, extra = {}) => (
        <ControlLineRow
            key={line.id}
            line={line}
            clientId={clientId}
            isLatestAlert={alertLines.includes(line.id)}
            latestAlertLabel={latestAlert && latestAlert.label}
            // El chip «Definir objetivo»: el modal de objetivo.
            onDefineGoal={onDefineGoal ? () => onDefineGoal(line.id) : undefined}
            // «Desactivar», al pie del detalle. La fila no escribe: avisa, y el shell confirma y
            // llama a `lineManageActions`. Eliminar y reactivar viven en la vista de todas las
            // líneas (card bBcBPqGg).
            onDeactivate={onDeactivateLine}
            // El objetivo sugerido desde el margen de ESTA línea (bloque `economy`, por id). Sin el
            // módulo el shell manda `economy: null` y la fila no ofrece nada.
            lineEconomy={economyLineFor(economy, line.id)}
            {...extra}
        />
    );

    const renderFinishedGroup = () => {
        if (!groupFinished) return null;
        return (
            <div className="pnl-lines__group">
                <button type="button" className="pnl-lines__group-toggle" aria-expanded={groupOpen}
                        onClick={() => setFinishedOpen(!groupOpen)}>
                    {groupOpen ? <CaretUp size={13} weight="bold"/> : <CaretDown size={13} weight="bold"/>}
                    {t('client_panel.lines_finished_group', 'Terminadas ({{count}})', {count: finished.length})}
                </button>
                {shownFinished.map((line) => renderRow(line, {finished: true}))}
            </div>
        );
    };

    // Día 1: las líneas no las creó el usuario a mano, las armó Dashbo desde las conexiones. Sin
    // decirlo, el presupuesto que aparece cargado se lee como un número inventado.
    const renderOrigin = () => (
        <div className="pnl-origen">
            <CheckCircle size={16} weight="fill"/>
            <span className="pnl-origen__txt">
                {t('client_panel.lines_origin',
                    'Creadas hoy desde tus conexiones — presupuesto sugerido desde el gasto real.')}
            </span>
            <Link
                className="pnl-origen__link"
                to={`/client/${clientId}/memory/bitacora?layers=budget,configuration`}
            >
                {t('client_panel.lines_origin_link', 'Ver cómo se armó')}
            </Link>
        </div>
    );

    const renderEmpty = () => (
        <div className="pnl-empty">
            <Target size={34}/>
            {/* Sin activas pero con desactivadas (se acaba de desactivar la única): «Crea la
                primera» mentiría — hay líneas, están apagadas (C-LINEAS-02 del review). */}
            <h3>
                {inactiveCount > 0
                    ? t('client_panel.lines_empty_only_inactive_title', 'No hay líneas activas')
                    : t('client_panel.lines_empty_title', 'Crea la primera línea de seguimiento')}
            </h3>
            {/* Sin ninguna línea todavía, el vacío dice QUÉ se va a crear: «línea de seguimiento»
                no se explica sola. Con desactivadas no hace falta — el usuario ya armó alguna. */}
            {!(inactiveCount > 0) && <p className="pnl-empty__text">{lineDefinition(t)}</p>}
            {/* Y dónde leer más: el artículo canónico del Centro de Ayuda. Va aparte del párrafo,
                que es la definición y nada más. */}
            {!(inactiveCount > 0) && (
                <div className="pnl-empty__more">
                    <LinkToArticle article={HELP_LINE_DEFINITION} plain text={lineDefinitionMore(t)}/>
                </div>
            )}
            {/* Las dos vías juntas: a mano, o dejando que Dashbo las arme desde el media plan. Es
                el momento en que la importación más sirve, y hasta ahora acá no se ofrecía. */}
            <div className="pnl-empty__actions">
                {/* `onNewLine` lo cablea el shell (abre el AddBusinessUnitModal); mientras no
                    llegue, el botón no rompe nada. */}
                <button type="button" className="pnl-btn pnl-btn--primary" onClick={onNewLine}>
                    {t('client_panel.header_new_line', 'Nueva línea de seguimiento')}
                </button>
                {renderImportMediaPlan()}
            </div>
            {renderInactiveLink()}
        </div>
    );

    return (
        <div className="box pnl-card" {...anchorAttr('panel_lines')}>
            {renderHead()}

            {/* La planilla vinculada vive DENTRO del bloque que alimenta (card gW32VovP): «el media
                plan es para armar líneas de control». Mismo gate que el botón de la cabecera. */}
            {mediaPlanEnabled && (
                <MediaPlanStrip data={mediaPlan} projection={projection} currency={currency}
                                clientId={clientId}/>
            )}

            {isNewClient && renderOrigin()}

            {/* La plata de las cuentas de ESTE cliente que no mira ninguna línea (UD-5): el renglón gris que
                lleva a «Inversión sin control» filtrada por el cliente. Se protege sola (sin el permiso o en
                el clásico no pide ni dibuja) y sin plata en juego no ocupa lugar. */}
            <UncontrolledSummaryCard clientId={clientId} className="-panel"/>

            <div className="pnl-cnt pnl-lines"
                 style={{'--pnl-chans-w': `${channelsWidth([...visible, ...shownFinished])}px`}}>
                {!lines.length ? renderEmpty() : (
                    <>
                        {visible.map((line) => renderRow(line, {finished: isFinished(line)}))}

                        {renderFinishedGroup()}

                        {/* El pie es la fila de salidas de la card. «Ver todas» sólo aparece cuando
                            hay líneas en curso que no entraron; el historial está SIEMPRE que haya
                            líneas: es el único acceso que le va a quedar cuando Monitoreo
                            desaparezca. */}
                        <div className="pnl-lines__actions">
                            {hidden > 0 && (
                                <Link className="pnl-lines__more" to={linesPath}>
                                    {t('client_panel.lines_view_all', 'Ver todas →')}
                                </Link>
                            )}
                            {renderInactiveLink()}
                            <Link className="pnl-lines__more" to={historyPath}>
                                {t('client_panel.lines_see_history', 'Ver el historial →')}
                            </Link>
                        </div>

                        <div className="pnl-lines__legend">
                            <span className="pnl-lines__legend-mark">│</span>
                            {t('client_panel.lines_legend', 'consumo esperado a hoy')}
                            {dayLabel ? ` — ${dayLabel}` : ''}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
